import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLaunchDarklySettings } from "@MasterBluePrint/Store/Context/ContextSelector";
import { initialize, LDClient } from "launchdarkly-js-client-sdk";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

interface Props {
    featureFlags: string[];
}

let ldClient: LDClient | null = null;
let isClientInitialized = false;

const generateGuestId = (): string => crypto.getRandomValues(new Uint8Array(16))
    .reduce((id, byte) => id + byte.toString(16).padStart(2, '0'), '');

const getUserKey = (isAuthenticated: boolean | undefined, session: any): string => {
    if (isAuthenticated && session?.userProfileId) {
        return session.userProfileId;
    }
    let guestId = sessionStorage.getItem("ldGuestId");
    if (!guestId) {
        guestId = generateGuestId();
        sessionStorage.setItem("ldGuestId", guestId);
    }
    return guestId;
};

const useLaunchDarklyFlags = ({ featureFlags }: Props): Record<string, boolean | undefined> => {
    const session = useSelector((state: ApplicationState) => getSession(state));
    const customLDSettings = useSelector((state: ApplicationState) => getLaunchDarklySettings(state));
    const [flagValues, setFlagValues] = useState<Record<string, boolean | undefined>>({});
    const isAuthenticated = session?.isAuthenticated;
    const userName = isAuthenticated ? session.email : "guest";

    useEffect(() => {
        if (customLDSettings?.useLaunchDarkly && customLDSettings?.launchDarklyClientId) {
            let unsubscribed = false;
            const onChangeHandlers: { [key: string]: (newValue: boolean) => void } = {};
            const initAndEvaluate = async () => {
                try {
                    if (!customLDSettings?.useLaunchDarkly || !customLDSettings?.launchDarklyClientId) {
                        logger.error("Client Id or useLaunchadarkly option were not set in the console.")
                        return;
                    }

                    const userKey = getUserKey(isAuthenticated, session);

                    // ******** SENDING context with additional options is costing HUNDREDS per day.********
                    // When we get licensing sorted out, we can look at turning context and options back on.
                    // const context = { kind: "user", key: userKey, name: userName };
                    // const options = { evaluationReasons: true, sendEvents: true, logger: logger };
                    if (!ldClient) {
                        // ldClient = initialize(customLDSettings.launchDarklyClientId, context, options);
                        ldClient = initialize(customLDSettings.launchDarklyClientId, { key : 'anonymous-user'});
                    }
                    // *************************************************************************************

                    if (!isClientInitialized) {
                        await new Promise(resolve => ldClient!.on("ready", resolve));
                        isClientInitialized = true;
                    }

                    const updatedValues: Record<string, boolean> = {};

                    for (const flag of featureFlags) {
                        const value = await ldClient.variation(flag, false);
                        updatedValues[flag] = value;

                        const onChangeHandler = (newValue: boolean) => {
                            setFlagValues(prev => ({ ...prev, [flag]: newValue }));
                        };
                        ldClient.on(`change:${flag}`, onChangeHandler);
                        onChangeHandlers[flag] = onChangeHandler;
                    }

                    if (!unsubscribed) {
                        setFlagValues(updatedValues);
                    }
                } catch (error) {
                    const fallBackValues = featureFlags.reduce((acc, flag) => {
                        acc[flag] = false;
                        return acc;
                    }, {} as Record<string, boolean>);

                    if (!unsubscribed) {
                        setFlagValues(fallBackValues);
                    }
                }
            };

            initAndEvaluate();

            return () => {
                unsubscribed = true;
                for (const flag of featureFlags) {
                    const handler = onChangeHandlers[flag];
                    if (handler) {
                        ldClient?.off(`change:${flag}`, handler);
                    }
                }
            };
        }

        setFlagValues(featureFlags.reduce((acc, flag) => ({ ...acc, [flag]: false }), {}));
    }, [featureFlags.slice().sort().join(","), isAuthenticated]);
    return flagValues;
};

export default useLaunchDarklyFlags;